import React from 'react';
import { Tag, Tooltip } from 'antd';
import {
  FunnelPlotFilled,
  HomeOutlined,
  SwapOutlined,
  SunFilled,
  ShoppingOutlined,
  RightOutlined,
} from '@ant-design/icons';

import '../../less/HomeSiteCardPipelineVisual.less';
import CropNameBadge from '../../components/CropNameBadge';

const tip = (label, title) => <Tooltip title={title}><span>{label}</span></Tooltip>;

const formatCount = (n) => Number(n).toLocaleString();

const formatWeight = (grams) => {
  if (grams == null || grams <= 0) {
    return null;
  }
  if (grams >= 1000) {
    const kg = grams / 1000;
    const rounded = kg >= 100 ? Math.round(kg) : Math.round(kg * 10) / 10;
    return `${formatCount(rounded)} kg`;
  }
  return `${formatCount(Math.round(grams))} g`;
};

const SITE_STEPS = [
  { key: 'sow', num: 1, label: 'Sow', sub: 'Plant seeds', icon: FunnelPlotFilled, iconClass: 'sow', action: 'sow', tip: 'Step 1 — log seeds planted' },
  { key: 'propagation', num: 2, label: 'Propagation', sub: 'Nursery stage', icon: HomeOutlined, iconClass: 'propagation', countKey: 'inPropagationCount', tip: 'Sown in - local transplanted out - propagation waste' },
  { key: 'transplant', num: 3, label: 'Transplant', sub: 'Move to system', icon: SwapOutlined, iconClass: 'transplant', action: 'transplant', tip: 'Step 3 — log move to production' },
  { key: 'production', num: 4, label: 'Grow', sub: 'In production', icon: SunFilled, iconClass: 'production', countKey: 'inProductionCount', tip: 'All transplanted in - harvested out - production waste' },
  { key: 'harvest', num: 5, label: 'Harvest', sub: 'Pick & sell', icon: ShoppingOutlined, iconClass: 'harvest', action: 'harvest', countKey: 'harvestWeek', countSuffix: '/ 7d', tip: 'Step 5 — log plants harvested' },
];

const CropBar = ({ inPropagation, inProduction, harvestWeek }) => {
  const total = inPropagation + inProduction + harvestWeek;
  if (total <= 0) {
    return <div className="crop-bar" />;
  }
  const pct = (n) => `${Math.max((n / total) * 100, n > 0 ? 2 : 0)}%`;
  return (
    <div className="crop-bar">
      {inPropagation > 0 && <div className="crop-bar-segment propagation" style={{ width: pct(inPropagation) }} />}
      {inProduction > 0 && <div className="crop-bar-segment production" style={{ width: pct(inProduction) }} />}
      {harvestWeek > 0 && <div className="crop-bar-segment harvest" style={{ width: pct(harvestWeek) }} />}
    </div>
  );
};

export default function HomeSiteCardPipelineVisual({ stats, harvestWeekAmount, harvestWeekWeight, cropPipeline, crops, onQuickLog }) {
  const getStepCount = (step) => {
    if (step.countKey === 'harvestWeek') {
      return harvestWeekAmount;
    }
    if (step.countKey) {
      return stats[step.countKey];
    }
    return null;
  };

  const displayCrops = [...cropPipeline].sort((a, b) => {
    const aTotal = a.inPropagation + a.inProduction + a.harvestWeek;
    const bTotal = b.inPropagation + b.inProduction + b.harvestWeek;
    if ((aTotal > 0) !== (bTotal > 0)) {
      return bTotal > 0 ? 1 : -1;
    }
    return a.name.localeCompare(b.name);
  });

  const harvestWeightLabel = formatWeight(harvestWeekWeight);

  return (
    <div className="home-pipeline-visual">
      <div className="section-title">
        {tip('How production works', 'Your crops move through 5 steps from seed to harvest')}
      </div>
      <div className="site-flow">
        {SITE_STEPS.map(step => {
          const count = getStepCount(step);
          const Icon = step.icon;
          const hasCount = count !== null && count !== undefined;
          const isActive = hasCount && count > 0;
          const showHarvestWeight = step.key === 'harvest' && harvestWeightLabel;
          return (
            <div
              key={step.key}
              className={['site-flow-step', step.action ? 'clickable' : ''].filter(Boolean).join(' ')}
              onClick={step.action ? () => onQuickLog(step.action) : undefined}
            >
              <Tooltip title={step.tip}>
                <div className={['step-box', isActive ? 'active' : ''].filter(Boolean).join(' ')}>
                  <div className="step-num">Step {step.num}</div>
                  <div className={`step-icon ${step.iconClass}`}><Icon /></div>
                  <div className="step-label">{step.label}</div>
                  <div className="step-sub">{step.sub}</div>
                  <div className={['step-count', showHarvestWeight ? 'with-weight' : ''].filter(Boolean).join(' ')}>
                    {hasCount && (
                      <>
                        {formatCount(count)}
                        {step.countSuffix && <span className="step-count-suffix"> {step.countSuffix}</span>}
                        {showHarvestWeight && (
                          <div className="step-count-weight">{harvestWeightLabel}</div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </Tooltip>
              <RightOutlined className="step-arrow" />
            </div>
          );
        })}
      </div>

      <div className="section-title" style={{ marginTop: 16 }}>
        {tip('Crops in the pipeline', 'Where each crop is right now')}
      </div>
      <div className="pipeline-legend">
        <span className="legend-item"><span className="legend-dot propagation" /> Propagation</span>
        <span className="legend-item"><span className="legend-dot production" /> Production</span>
        <span className="legend-item"><span className="legend-dot harvest" /> Harvested (7d)</span>
      </div>

      {displayCrops.length === 0 ? (
        <div className="empty-hint">No crops in catalog — add crops first, then start sowing</div>
      ) : (
        <div className="crop-grid">
          {displayCrops.map(row => {
            const crop = crops[row.cropId];
            const isEmpty = row.inPropagation <= 0 && row.inProduction <= 0 && row.harvestWeek <= 0;
            return (
              <div key={row.cropId} className={['crop-card', isEmpty ? 'empty' : ''].filter(Boolean).join(' ')}>
                <div className="crop-card-header">
                  <span className="crop-name"><CropNameBadge name={row.name} /></span>
                  {crop && (
                    <div className="crop-days">
                      <Tooltip title="Propagation days">
                        <Tag color="blue">{crop.seedlingDayCount}d</Tag>
                      </Tooltip>
                      <Tooltip title="Grow days">
                        <Tag color="green">{crop.plantingDayCount}d</Tag>
                      </Tooltip>
                    </div>
                  )}
                </div>
                <div className="crop-stages">
                  <Tooltip title="Sown in - local transplanted out - propagation waste">
                    <div className="crop-stage propagation">
                      <div className="stage-label">Propagation</div>
                      <div className="stage-count">{row.inPropagation}</div>
                    </div>
                  </Tooltip>
                  <RightOutlined className="crop-stage-arrow" />
                  <Tooltip title="All transplanted in - harvested out - production waste">
                    <div className="crop-stage production">
                      <div className="stage-label">Production</div>
                      <div className="stage-count">{row.inProduction}</div>
                    </div>
                  </Tooltip>
                  <RightOutlined className="crop-stage-arrow" />
                  <Tooltip title="Plants harvested this week">
                    <div className="crop-stage harvest">
                      <div className="stage-label">Harvested</div>
                      <div className="stage-count">{row.harvestWeek}</div>
                    </div>
                  </Tooltip>
                </div>
                <CropBar inPropagation={Math.max(row.inPropagation, 0)} inProduction={Math.max(row.inProduction, 0)} harvestWeek={row.harvestWeek} />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
